// src/pages/TechnicianDetails.js
import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    FiCheckSquare, FiArrowLeft, FiUser, FiPhone, FiAward, FiTool, 
    FiCheck, FiX, FiCheckCircle, FiClock, FiSlash // Added Icons for Radial Menu
} from 'react-icons/fi';
import moment from 'moment';
import { AuthContext } from '../context/AuthContext';
import useLongPress from '../hooks/useLongPress';
import TicketQueue from '../components/TicketQueue'; 
import RadialStatusMenu from '../components/RadialStatusMenu'; 

const AssignSpecializationsModal = ({ isOpen, onClose, onSave, allSpecializations = [], currentSpecializations = [] }) => {
    const [selectedNames, setSelectedNames] = useState(currentSpecializations);
    const overlayMousedownTarget = useRef(null);
    useEffect(() => { if (isOpen) { setSelectedNames(currentSpecializations); } }, [isOpen, currentSpecializations]);
    if (!isOpen) return null;
    const handleToggleSelection = (specName) => { setSelectedNames(prev => prev.includes(specName) ? prev.filter(name => name !== specName) : [...prev, specName]); };
    const handleSave = () => { onSave(selectedNames); onClose(); };
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    return (
        <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <div className="modal-content modal-content-slim">
                <div className="modal-header"><h3 className="modal-title">Select Specializations</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
                <div className="modal-body">
                    <div className="selection-list multi-select-list">
                        {allSpecializations.map(spec => {
                            const isSelected = selectedNames.includes(spec.name);
                            return (
                                <div key={spec._id} className={`selection-list-item ${isSelected ? 'selected' : ''}`} onClick={() => handleToggleSelection(spec.name)}>
                                    <div className="item-main-info"><p className="item-title">{spec.name}</p></div>
                                    <div className="item-meta-info">{isSelected && <FiCheck className="selection-checkmark" />}</div>
                                 </div>
                            );
                        })}
                    </div>
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={handleSave}>Confirm ({selectedNames.length})</button></div>
            </div>
        </div>
    );
};

const TechnicianDetails = ({ tickets, technicians, appointments, onUpdateTechnician, specializations }) => {
    const { technicianId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const canEdit = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
    [user]);
    
    const canViewSensitiveDetails = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)),
    [user]);

    const [editingField, setEditingField] = useState(null);
    const [editedTechnician, setEditedTechnician] = useState(null);
    const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState(false);

    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [statusMenuPosition, setStatusMenuPosition] = useState({ x: 0, y: 0 });

    const technician = useMemo(() => technicians.find(t => t._id === technicianId), [technicians, technicianId]);

    const currentViewerTechnicianProfile = useMemo(() => {
        if (!user || !technicians) return null;
        return technicians.find(t => {
            const tUserId = t.userId?._id || t.userId;
            return tUserId === user.id;
        });
    }, [user, technicians]);

    useEffect(() => {
        if (technician) {
            setEditedTechnician({ ...technician });
        }
    }, [technician]);

    const queuedTickets = useMemo(() => {
        if (!technician || !tickets) return [];
        const priorityOrder = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };

        const myOpenTickets = tickets.filter(ticket =>
            !ticket.parentId &&
            (ticket.assignedTechnicianIds || []).includes(technician._id) &&
            ticket.status !== 'Closed'
        );

        return myOpenTickets.sort((a, b) => {
            const aCheckedIn = a.activeCheckIns?.some(ci => ci.technicianId === technician._id);
            const bCheckedIn = b.activeCheckIns?.some(ci => ci.technicianId === technician._id);
            if (aCheckedIn && !bCheckedIn) return -1;
            if (bCheckedIn && !aCheckedIn) return 1;

            if (a.status === 'In Progress' && b.status !== 'In Progress') return -1;
            if (b.status === 'In Progress' && a.status !== 'In Progress') return 1;

            const priorityA = priorityOrder[a.priority] || 5;
            const priorityB = priorityOrder[b.priority] || 5;
            if (priorityA !== priorityB) return priorityA - priorityB;
            
            return new Date(a.createdAt) - new Date(b.createdAt);
        });
    }, [tickets, technician]);

    const handleBack = () => navigate('/technicians');

    const handleDoubleClick = (fieldName) => {
        if (canEdit) {
            if (fieldName === 'specializations') {
                setIsSpecModalOpen(true);
            } else {
                setEditingField(fieldName);
            }
        }
    };

    const handleCopyPhone = () => {
        if (!technician.phone) return;
        navigator.clipboard.writeText(technician.phone).then(() => {
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000); 
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    // ✅ RADIAL MENU HANDLERS (TAP & LONG PRESS)
    const handleStatusClick = (e) => {
        if (!canEdit) return;
        e.preventDefault();
        // FIXED: Using correct state setter name
        setStatusMenuPosition({ x: e.clientX || window.innerWidth / 2, y: e.clientY || window.innerHeight / 2 });
        setIsStatusMenuOpen(true);
    };

    const statusLongPress = useLongPress((e) => {
        if (!canEdit) return;
        const clientX = e.touches ? e.touches[0].clientX : (e.clientX || window.innerWidth / 2);
        const clientY = e.touches ? e.touches[0].clientY : (e.clientY || window.innerHeight / 2);
        
        setStatusMenuPosition({ x: clientX, y: clientY });
        setIsStatusMenuOpen(true);
    }, { delay: 400, shouldPreventDefault: false }); // Unblocks native tap/click

    const handleStatusSelect = async (newStatus) => {
        if (canEdit && newStatus !== technician.status) {
            await onUpdateTechnician(technician._id, { status: newStatus });
        }
        setIsStatusMenuOpen(false);
    };

    const techStatusOptions = useMemo(() => [
        { label: 'Active', icon: FiCheckCircle },
        { label: 'On Leave', icon: FiClock },
        { label: 'Inactive', icon: FiSlash }
    ], []);

    const phoneLongPress = useLongPress(() => handleDoubleClick('phone'), { delay: 500 });
    const skillLevelLongPress = useLongPress(() => handleDoubleClick('skillLevel'), { delay: 500 });
    const specializationsLongPress = useLongPress(() => handleDoubleClick('specializations'), { delay: 500 });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedTechnician(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (fieldName) => {
        if (!canEdit || !editedTechnician) return;
        const updatePayload = { [fieldName]: editedTechnician[fieldName] };
        await onUpdateTechnician(technician._id, updatePayload);
        setEditingField(null);
    };

    const handleKeyDown = (e, fieldName) => {
        if (e.key === 'Enter') {
            handleSave(fieldName);
        } else if (e.key === 'Escape') {
            setEditedTechnician({ ...technician });
            setEditingField(null);
        }
    };

    const handleSaveSpecializations = async (selectedNames) => {
        if (!canEdit || !editedTechnician) return;
        setEditedTechnician(prev => ({ ...prev, specializations: selectedNames }));
        await onUpdateTechnician(technician._id, { specializations: selectedNames });
        setIsSpecModalOpen(false);
        setEditingField(null);
    };

    if (!technician) {
        return (
            <div className="page-container">
                <div className="page-header"><div className="header-left-details"><button onClick={handleBack} className="back-btn" title="Go Back"><FiArrowLeft /></button><h2 className="page-title">Technician Not Found</h2></div></div>
                <div className="card"><p>The technician you are looking for does not exist.</p></div>
            </div>
        );
    }

    const statusClass = technician.status.toLowerCase().replace(/\s+/g, '-');

    return (
        <>
            <AssignSpecializationsModal isOpen={isSpecModalOpen} onClose={() => setIsSpecModalOpen(false)} onSave={handleSaveSpecializations} allSpecializations={specializations} currentSpecializations={editedTechnician?.specializations || []} />
            
            {isStatusMenuOpen && (
                <RadialStatusMenu
                    options={techStatusOptions}
                    onSelect={handleStatusSelect}
                    onClose={() => setIsStatusMenuOpen(false)}
                    clickPosition={statusMenuPosition}
                />
            )}

            <div className="page-container">
                <div className="page-header details-page-header">
                    <div className="header-left-details">
                        <button onClick={handleBack} className="back-btn" title="Go Back"><FiArrowLeft /></button>
                        <div className="details-page-avatar">
                            {technician.userId?.profilePictureUrl ? (<img src={technician.userId.profilePictureUrl} alt={technician.name} className="details-page-avatar-img" />) : (<span className="details-page-avatar-initials">{technician.name.charAt(0)}</span>)}
                        </div>
                        <div>
                            <h2 className="page-title">{technician.name}</h2>
                            <p className="page-subtitle">{technician.email}</p>
                        </div>
                    </div>
                </div>

                <div className="details-page-grid">
                    <div className="details-main-column">
                        <div className="technician-profile-card">
                            <div className="technician-details-grid">
                                
                                <div className="technician-detail-item">
                                    <span className="technician-detail-label"><FiUser />Status</span>
                                    <div 
                                        className={`detail-value ${canEdit ? 'interactive-status-wrapper' : ''}`}
                                    >
                                        {/* ✅ COMBINED TAP & LONG-PRESS HANDLERS */}
                                        <span 
                                            className={`status-badge status-${statusClass} ${canEdit ? 'clickable' : ''}`}
                                            {...statusLongPress}
                                            onClick={handleStatusClick}
                                            title={canEdit ? "Tap or long-press to change status" : ""}
                                        >
                                            {technician.status}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="technician-detail-item">
                                    <span className="technician-detail-label"><FiPhone />Phone Number</span>
                                    {editingField === 'phone' ? (
                                        <input type="tel" name="phone" value={editedTechnician.phone} onChange={handleChange} onBlur={() => handleSave('phone')} onKeyDown={(e) => handleKeyDown(e, 'phone')} className="form-input" autoFocus/>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span 
                                                className={`technician-detail-value ${canEdit ? 'editable-field' : ''}`} 
                                                onClick={handleCopyPhone}
                                                onDoubleClick={() => handleDoubleClick('phone')} 
                                                {...phoneLongPress}
                                                title="Click to Copy"
                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '4px' }}
                                            >
                                                {technician.phone || 'N/A'}
                                            </span>
                                            
                                            {copyFeedback && (
                                                <span className="fade-in-out" style={{ fontSize: '0.8rem', color: 'var(--success-color, #10b981)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <FiCheck /> Copied!
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {canViewSensitiveDetails && (
                                    <>
                                        <div className="technician-detail-item">
                                            <span className="technician-detail-label"><FiAward />Skill Level</span>
                                            {editingField === 'skillLevel' ? (
                                                <select name="skillLevel" value={editedTechnician.skillLevel} onChange={handleChange} onBlur={() => handleSave('skillLevel')} onKeyDown={(e) => handleKeyDown(e, 'skillLevel')} className="form-select" autoFocus>
                                                    <option>Trainee</option><option>Junior</option><option>Senior</option><option>Master</option>
                                                </select>
                                            ) : (
                                                <span className={`technician-detail-value ${canEdit ? 'editable-field' : ''}`} onDoubleClick={() => handleDoubleClick('skillLevel')} {...skillLevelLongPress}>{technician.skillLevel}</span>
                                            )}
                                        </div>
                                        <div className="technician-detail-item">
                                            <span className="technician-detail-label"><FiTool />Specializations</span>
                                            <div className={`technician-specializations-container ${canEdit ? 'editable-field' : ''}`} onDoubleClick={() => handleDoubleClick('specializations')} {...specializationsLongPress}>
                                                {technician.specializations.length > 0 ? technician.specializations.map(spec => <span key={spec} className="specialization-tag">{spec}</span>) : 'None'}
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>

                        <div className="assigned-tickets-card">
                            <div className="assigned-tickets-header"><FiCheckSquare /><span>Ticket Queue</span></div>
                            
                            <TicketQueue 
                                tickets={queuedTickets}
                                allTickets={tickets}
                                appointments={appointments}
                                onTicketClick={(id) => navigate(`/tickets/${id}`)}
                                highlightTechnicianId={currentViewerTechnicianProfile?._id}
                                emptyMessage="This technician has no open tickets in their queue."
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TechnicianDetails;